<template>
  <section class="ops-report" :class="{ 'ops-report--phone': phone }" data-test="ops-report" :aria-busy="loading ? 'true' : 'false'">
    <button type="button" class="ops-report__back" data-test="ops-report-back" @click="$emit('back')">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M15 18l-6-6 6-6" /></svg>
      Board
    </button>

    <header class="ops-report__head">
      <div class="ops-report__intro">
        <h1 class="ops-report__title" data-test="ops-report-title">{{ phone ? 'Report' : 'Practice Ops report' }}</h1>
        <p class="ops-report__lede" data-test="ops-report-lede">{{ lede }}</p>
      </div>
      <button type="button" class="ops-report__btn" data-test="ops-report-export" :disabled="!canExport" @click="exportCsv">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" /></svg>
        Export CSV
      </button>
    </header>

    <!-- ONE filter row above everything it scopes: the range, then the provider. -->
    <div class="ops-report__filters" data-test="ops-report-filters">
      <div class="ops-report__pills" role="group" aria-label="Date range">
        <button
          v-for="p in presets"
          :key="p.key"
          type="button"
          class="ops-report__pill"
          :class="{ 'is-on': p.key === preset }"
          :aria-pressed="p.key === preset ? 'true' : 'false'"
          data-test="ops-report-range"
          :data-range="p.key"
          @click="setPreset(p.key)"
        >{{ p.label }}</button>
      </div>

      <div v-if="preset === 'custom'" class="ops-report__custom" data-test="ops-report-custom">
        <input type="date" class="ops-report__date" aria-label="From date" data-test="ops-report-from" :value="customFrom" :max="customTo || null" @change="onDate('customFrom', $event)" />
        <span class="ops-report__to">to</span>
        <input type="date" class="ops-report__date" aria-label="To date" data-test="ops-report-to" :value="customTo" :min="customFrom || null" @change="onDate('customTo', $event)" />
      </div>

      <label class="ops-report__prov">
        <span class="ops-report__sr">Provider</span>
        <select v-model="provNum" class="ops-report__select" aria-label="Provider" data-test="ops-report-provider" @change="load">
          <option value="">All providers</option>
          <option v-for="p in providerOptions" :key="p.provNum" :value="String(p.provNum)">{{ p.name }}</option>
        </select>
      </label>
    </div>
    <p v-if="rangeError" class="ops-report__range-error" role="alert" data-test="ops-report-range-error">{{ rangeError }}</p>

    <!-- First load: a skeleton. A refetch keeps the frame (the previous numbers stay, dimmed) — no layout jump. -->
    <div v-if="loading && !report" class="ops-report__skeleton" data-test="ops-report-loading" aria-hidden="true">
      <div class="ops-report__skel-tiles"><span v-for="n in 4" :key="n" class="ops-report__skel-tile"></span></div>
      <span class="ops-report__skel-chart"></span>
    </div>

    <div v-else-if="error" class="ops-report__state ops-report__state--error" role="alert" data-test="ops-report-error">
      <h3 class="ops-report__state-title">Could not load the report</h3>
      <p class="ops-report__state-text" data-test="ops-report-error-text">{{ error }}</p>
      <button type="button" class="ops-report__btn ops-report__btn--primary" data-test="ops-report-retry" @click="load">Try again</button>
    </div>

    <div v-else-if="report && !rows.length" class="ops-report__state" data-test="ops-report-empty" :class="{ 'is-dim': loading }">
      <h3 class="ops-report__state-title">No visits in this range</h3>
      <p class="ops-report__state-text">{{ emptyText }}</p>
    </div>

    <div v-else-if="report" class="ops-report__body" :class="{ 'is-dim': loading }" data-test="ops-report-body">
      <div class="ops-report__tiles" data-test="ops-report-tiles">
        <div v-for="t in tiles" :key="t.key" class="ops-report__tile" :class="`is-${t.tone}`" data-test="ops-report-tile" :data-tile="t.key">
          <div class="ops-report__tile-label">{{ t.label }}</div>
          <div class="ops-report__tile-value" data-test="ops-report-tile-value">{{ t.value }}</div>
          <div class="ops-report__tile-sub">{{ t.sub }}</div>
        </div>
      </div>

      <div class="ops-report__charts">
        <div class="ops-report__card" data-test="ops-report-wait-card">
          <div class="ops-report__card-head">
            <span class="ops-report__card-title">{{ series.title }}</span>
            <span v-if="thresholdOn" class="ops-report__key" data-test="ops-report-threshold-key"><span class="ops-report__key-line"></span>threshold {{ report.thresholdMin }} min</span>
            <span class="ops-report__seg" role="group" aria-label="Average wait view">
              <button type="button" class="ops-report__seg-btn" :class="{ 'is-on': waitView === 'chart' }" :aria-pressed="waitView === 'chart' ? 'true' : 'false'" data-test="ops-report-wait-chart-btn" @click="waitView = 'chart'">Chart</button>
              <button type="button" class="ops-report__seg-btn" :class="{ 'is-on': waitView === 'table' }" :aria-pressed="waitView === 'table' ? 'true' : 'false'" data-test="ops-report-wait-table-btn" @click="waitView = 'table'">Table</button>
            </span>
          </div>
          <div class="ops-report__card-sub" data-test="ops-report-wait-sub">Minutes, {{ series.unit }} · {{ rangeLabel }}</div>
          <p v-if="waitView === 'chart' && !hasWaitPoints" class="ops-report__none" data-test="ops-report-wait-none">No visit in this range has a recorded wait yet. A wait is recorded once the patient is seated.</p>
          <OpsReportChart v-else-if="waitView === 'chart'" :config="waitConfig" :height="phone ? 190 : 230" :label="waitAlt" data-test="ops-report-wait-chart" />
          <div v-else class="ops-report__scroll">
            <table class="ops-report__mini" data-test="ops-report-wait-table">
              <thead><tr><th scope="col">{{ series.mode === 'hour' ? 'Hour' : 'Day' }}</th><th scope="col">Visits</th><th scope="col">Average wait</th><th scope="col">Over threshold</th></tr></thead>
              <tbody>
                <tr v-for="(label, i) in series.labels" :key="`${label}-${i}`">
                  <th scope="row">{{ label }}</th><td>{{ series.visits[i] }}</td><td>{{ series.avg[i] === null ? '—' : fmtMin(series.avg[i]) }}</td><td>{{ thresholdOn ? series.over[i] : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="ops-report__card" data-test="ops-report-chair-card">
          <div class="ops-report__card-head">
            <span class="ops-report__card-title">Booked vs actual chair time</span>
            <span class="ops-report__key"><span class="ops-report__key-box ops-report__key-box--booked"></span>Booked</span>
            <span class="ops-report__key"><span class="ops-report__key-box ops-report__key-box--actual"></span>Actual</span>
          </div>
          <div class="ops-report__card-sub">Average minutes per visit. Actual is seated to check-out. Every number is in the Providers table below.</div>
          <OpsReportChart v-if="chairRows.length" :config="chairConfig" :height="phone ? 190 : 230" :label="chairAlt" data-test="ops-report-chair-chart" />
          <p v-else class="ops-report__none" data-test="ops-report-chair-none">No visit has both a booked length and a check-out time yet.</p>
        </div>
      </div>

      <div v-if="providerTable.length" class="ops-report__card ops-report__card--flush" data-test="ops-report-providers">
        <div class="ops-report__card-head ops-report__card-head--pad"><span class="ops-report__card-title">Providers</span><span class="ops-report__card-note">{{ providerTable.length }} {{ providerTable.length === 1 ? 'provider' : 'providers' }}</span></div>
        <div class="ops-report__scroll">
          <table class="ops-report__mini ops-report__mini--pad" data-test="ops-report-provider-table">
            <thead><tr><th scope="col">Provider</th><th scope="col">Visits</th><th scope="col">Average wait</th><th scope="col">Average in chair</th><th scope="col">Average booked</th></tr></thead>
            <tbody>
              <tr v-for="p in providerTable" :key="p.provNum" data-test="ops-report-provider-row">
                <th scope="row">{{ p.name }}</th><td>{{ p.visits }}</td><td>{{ fmtMin(p.avgWaitMin) }}</td><td>{{ fmtMin(p.avgInChairMin) }}</td><td>{{ fmtMin(p.avgBookedMin) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="ops-report__card ops-report__card--flush" data-test="ops-report-visits">
        <div class="ops-report__card-head ops-report__card-head--pad">
          <span class="ops-report__card-title">Visits</span>
          <span class="ops-report__card-note" data-test="ops-report-visits-sub">Showing {{ shownRows.length }} of {{ rows.length }}</span>
        </div>
        <p v-if="legacyCount" class="ops-report__legacy" data-test="ops-report-legacy-note">
          {{ legacyCount }} {{ legacyCount === 1 ? 'visit comes' : 'visits come' }} from the older check-out notes (marked Legacy). Their arrival, ready, seated and dismissed times can be missing, so they may show no wait.
        </p>

        <!-- Phone: one card per visit (no sideways scroll). -->
        <ul v-if="phone" class="ops-report__cards" data-test="ops-report-cards">
          <li v-for="row in shownRows" :key="row.key" class="ops-vcard" data-test="ops-report-card">
            <div class="ops-vcard__top">
              <div class="ops-vcard__who">
                <span class="ops-vcard__name">{{ row.name }}</span>
                <span class="ops-vcard__meta">{{ row.dateText }}<template v-if="row.provName"> · {{ row.provName }}</template></span>
              </div>
              <span class="ops-report__wait" :class="`is-${row.waitLevel || 'none'}`" data-test="ops-report-card-wait">{{ row.waitText }}<span v-if="row.waitLive" class="ops-report__live"> · waiting</span></span>
            </div>
            <dl class="ops-vcard__grid">
              <div><dt>Booked</dt><dd>{{ row.booked || '—' }}</dd></div>
              <div><dt>Arrived</dt><dd>{{ row.arrived || '—' }}<template v-if="row.late"> · {{ row.lateText }} late</template></dd></div>
              <div><dt>Seated</dt><dd>{{ row.seated || '—' }}</dd></div>
              <div><dt>Room</dt><dd>{{ row.room || '—' }}</dd></div>
              <div><dt>In chair</dt><dd>{{ row.inChairText }}</dd></div>
              <div><dt>Check-out</dt><dd>{{ row.checkout || '—' }}</dd></div>
              <div v-if="row.dismissed"><dt>Dismissed</dt><dd>{{ row.dismissed }}</dd></div>
            </dl>
            <span v-if="row.legacy" class="ops-report__legacy-chip" title="From the older check-out notes: some times may be missing" data-test="ops-report-legacy">Legacy</span>
          </li>
        </ul>

        <div v-else class="ops-report__scroll">
          <table class="ops-report__table" data-test="ops-report-table">
            <thead>
              <tr>
                <th v-for="col in columns" :key="col.key" scope="col" :aria-sort="ariaSort(col)">
                  <button v-if="col.sortable" type="button" class="ops-report__sort" :class="{ 'is-on': sortKey === col.key }" :data-test="`ops-report-sort-${col.key}`" @click="sortBy(col.key)">
                    {{ col.label }}<span class="ops-report__arrow" aria-hidden="true">{{ sortKey === col.key ? (sortDir === 1 ? '▲' : '▼') : '' }}</span>
                  </button>
                  <span v-else>{{ col.label }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in shownRows" :key="row.key" data-test="ops-report-row">
                <td class="ops-report__who">
                  <span class="ops-report__name" data-test="ops-report-name">{{ row.name }}</span>
                  <span class="ops-report__day">{{ row.dateText }}<span v-if="row.legacy" class="ops-report__legacy-chip" title="From the older check-out notes: some times may be missing" data-test="ops-report-legacy">Legacy</span></span>
                </td>
                <td>{{ row.provName || '—' }}</td>
                <td>{{ row.booked || '—' }}</td>
                <td>{{ row.arrived || '—' }}</td>
                <td :class="{ 'is-muted': !row.late }">{{ row.lateText }}</td>
                <td>{{ row.ready || '—' }}</td>
                <td>{{ row.seated || '—' }}</td>
                <td><span class="ops-report__wait" :class="`is-${row.waitLevel || 'none'}`" data-test="ops-report-wait">{{ row.waitText }}<span v-if="row.waitLive" class="ops-report__live ops-report__live--block">waiting</span><span v-if="row.waitLevel === 'amber' || row.waitLevel === 'red'" class="ops-report__sr"> (over the waiting limit)</span></span></td>
                <td>{{ row.room || '—' }}</td>
                <td>{{ row.inChairText }}</td>
                <td>{{ row.checkout || '—' }}</td>
                <td>{{ row.dismissed || '—' }}</td>
                <td :class="{ 'is-muted': row.postOp === '—' }">{{ row.postOp }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="shownRows.length < rows.length" class="ops-report__more">
          <button type="button" class="ops-report__btn" data-test="ops-report-more" @click="showMore">Show {{ Math.min(PAGE_SIZE, rows.length - shownRows.length) }} more</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
/**
 * Practice Ops report page (plan Phase 5, 2026-10-30; design "Desktop — Report" / "Phone — Report").
 *
 * WHAT IT DRAWS. One POST /get-practice-pulse-report `{ siteID, from, to, provNum? }` (store `practicepulse/getPracticeOpsReport`) →
 * the four tiles, the "Average wait" chart, the "Booked vs actual chair time" bars, the Providers table and the Visits table, with a
 * CSV of the visits. The server did the maths with the one wait rule; the pure helpers in utils/practiceOps/report.js only word it.
 * Names come from the API, so the demo mask (a response interceptor) masks them and a PatNum missing from the roster still has a row.
 *
 * DATES ARE THE PRACTICE'S. `todayYmd` (a prop, the page's site-local day) is the only "today" here; every preset is string arithmetic
 * on it. A request that is not valid is never sent (report.js buildReportBody) and says why.
 *
 * STATES. First load: a skeleton. A refetch keeps the frame (the previous numbers stay, dimmed). A failure shows the server's words with
 * "Try again" (the store action throws). No visits: a message, not an empty table. A response that arrives after a newer request was made
 * is dropped, so a slow answer can never overwrite the range now on screen.
 *
 * PHONE (< 700 px, the `phone` prop). Tiles 2 x 2, the charts stacked, one card per visit instead of the wide table, no sideways scroll
 * of the page (the date pills and a long table scroll inside their own box).
 */
import OpsReportChart from './OpsReportChart.vue';
import * as waitRules from '@/utils/practiceOps/waitRules';
import { toCsv, exportFilename } from '@/utils/followups/listExport';
import {
  REPORT_PRESETS, rangeForPreset, buildReportBody, rangeText, ledeText, reportTiles, visitRows, sortVisitRows,
  waitSeries, providerRows, chairChartRows, visitsCsv, formatMinutes, hasThreshold,
} from '@/utils/practiceOps/report';
import { waitLineConfig, chairBarsConfig } from '@/utils/practiceOps/reportCharts';

const PAGE_SIZE = 100;

const COLUMNS = [
  { key: 'name', label: 'Patient', sortable: true },
  { key: 'provider', label: 'Provider', sortable: true },
  { key: 'booked', label: 'Booked', sortable: true },
  { key: 'arrived', label: 'Arrived', sortable: true },
  { key: 'late', label: 'Late', sortable: true },
  { key: 'ready', label: 'Ready', sortable: true },
  { key: 'seated', label: 'Seated', sortable: true },
  { key: 'wait', label: 'Waited', sortable: true },
  { key: 'room', label: 'Room', sortable: true },
  { key: 'inChair', label: 'In chair', sortable: true },
  { key: 'checkout', label: 'Check-out', sortable: true },
  { key: 'dismissed', label: 'Dismissed', sortable: true },
  { key: 'postOp', label: 'Post-op email', sortable: false },
];

export default {
  name: 'OpsReportPage',

  components: { OpsReportChart },

  props: {
    /** The practice (a UUID). The page waits for it. */
    siteId: { type: String, default: '' },
    /** GENERAL_TIMEZONE: every time in the report is read at the practice. */
    zone: { type: String, default: '' },
    /** The practice's calendar day, 'YYYY-MM-DD' (siteDate.js siteLocalDate). */
    todayYmd: { type: String, default: '' },
    /** The corrected clock (ms): the live wait of a patient who arrived today and is not seated yet. */
    nowMs: { type: Number, default: 0 },
    /** { <OperatoryNum>: <Practice Ops room name> } — the report only carries the number. */
    roomLabels: { type: Object, default: () => ({}) },
    /** A phone-sized window: cards instead of the wide table. */
    phone: { type: Boolean, default: false },
  },

  data() {
    return {
      presets: REPORT_PRESETS,
      columns: COLUMNS,
      PAGE_SIZE,
      preset: 'today',
      customFrom: '',
      customTo: '',
      provNum: '',
      report: null,
      loading: false,
      error: '',
      rangeError: '',
      /** The range the numbers on screen belong to. */
      shown: { from: '', to: '' },
      /** provNum → name, for every provider any response has named (the select must not shrink when one is chosen). */
      seen: {},
      waitView: 'chart',
      sortKey: '',
      sortDir: 1,
      visibleCount: PAGE_SIZE,
    };
  },

  computed: {
    range() {
      return rangeForPreset(this.preset, this.todayYmd, { from: this.customFrom, to: this.customTo });
    },

    providerOptions() {
      const out = Object.keys(this.seen).map((k) => ({ provNum: Number(k), name: this.seen[k] }));
      return out.sort((a, b) => a.name.localeCompare(b.name));
    },

    selectedProviderName() {
      const found = this.providerOptions.find((p) => String(p.provNum) === this.provNum);
      return found ? found.name : '';
    },

    lede() {
      if (!this.report) return this.range ? rangeText(this.range.from, this.range.to) : '';
      return ledeText({
        from: this.shown.from, to: this.shown.to, visitCount: this.rows.length, provName: this.selectedProviderName,
      });
    },

    rangeLabel() {
      return rangeText(this.shown.from, this.shown.to);
    },

    thresholdOn() {
      return !!this.report && hasThreshold(this.report.thresholdMin);
    },

    tiles() {
      return this.report ? reportTiles(this.report) : [];
    },

    series() {
      return waitSeries(this.report, this.zone);
    },

    /** Some point has a wait: a plot of nothing but gaps is a message instead. */
    hasWaitPoints() {
      return this.series.avg.some((v) => typeof v === 'number');
    },

    waitConfig() {
      return waitLineConfig(this.series, this.report ? this.report.thresholdMin : null);
    },

    waitAlt() {
      return `Average wait, ${this.series.unit}, ${this.rangeLabel}. The Table button lists every value.`;
    },

    providerTable() {
      return providerRows(this.report);
    },

    chairRows() {
      return chairChartRows(this.providerTable);
    },

    chairConfig() {
      return chairBarsConfig(this.chairRows);
    },

    chairAlt() {
      return `Booked versus actual chair time for ${this.chairRows.length} ${this.chairRows.length === 1 ? 'provider' : 'providers'}. The Providers table lists every value.`;
    },

    rows() {
      if (!this.report) return [];
      const built = visitRows(this.report.visits, {
        tz: this.zone,
        roomLabel: (n) => this.roomLabels[n] || '',
        nowMs: this.nowMs,
        todayYmd: this.todayYmd,
        rules: waitRules,
        thresholdMin: this.report.thresholdMin,
      });
      return sortVisitRows(built, this.sortKey, this.sortDir);
    },

    shownRows() {
      return this.rows.slice(0, this.visibleCount);
    },

    legacyCount() {
      return this.rows.filter((r) => r.legacy).length;
    },

    canExport() {
      return !!this.report && this.rows.length > 0 && !this.loading;
    },

    emptyText() {
      const who = this.selectedProviderName ? ` for ${this.selectedProviderName}` : '';
      return `Nothing${who} was recorded for ${this.rangeLabel || 'this range'}. A visit appears here once the patient is marked Arrived on the board.`;
    },
  },

  watch: {
    /** Midnight at the practice moved "today": a preset range follows it. */
    todayYmd(next, prev) {
      if (prev && next !== prev && this.preset !== 'custom') this.load();
    },

    /** The practice finished loading after the page opened. */
    siteId(next, prev) {
      if (next && !prev) this.load();
    },
  },

  created() {
    // bookkeeping that must not re-render the page
    this.requestId = 0;
  },

  mounted() {
    this.load();
  },

  beforeDestroy() {
    // a late answer for a page that is gone must not touch it
    this.requestId = -1;
  },

  methods: {
    fmtMin: formatMinutes,

    setPreset(key) {
      if (key === 'custom' && (!this.customFrom || !this.customTo)) {
        // open Custom on the range that is on screen, so there is something to edit
        const current = this.range || rangeForPreset('last7', this.todayYmd);
        this.customFrom = current ? current.from : '';
        this.customTo = current ? current.to : '';
      }
      this.preset = key;
      this.load();
    },

    onDate(field, event) {
      this[field] = event && event.target ? event.target.value : '';
      if (this.customFrom && this.customTo) this.load();
    },

    /** The one way a request is made: validate, send, keep the answer only if it is still the newest. */
    async load() {
      const range = this.range;
      const checked = range
        ? buildReportBody({ siteID: this.siteId, from: range.from, to: range.to, provNum: this.provNum })
        : { ok: false, error: this.todayYmd ? 'Pick a start date and an end date.' : '' };
      if (!checked.ok) {
        // the practice not being loaded yet is not an error to show: the siteId watcher loads once it is
        this.rangeError = this.siteId && this.todayYmd ? checked.error : '';
        return;
      }
      this.rangeError = '';
      this.error = '';
      this.requestId += 1;
      const mine = this.requestId;
      this.loading = true;
      try {
        const data = await this.$store.dispatch('practicepulse/getPracticeOpsReport', checked.body);
        if (this.requestId !== mine) return;
        this.report = data && typeof data === 'object' ? data : { visits: [], tiles: {}, daily: [], providers: [] };
        this.shown = { from: checked.body.from, to: checked.body.to };
        this.rememberProviders(this.report.providers);
        this.visibleCount = PAGE_SIZE;
        this.waitView = 'chart';
      } catch (err) {
        if (this.requestId !== mine) return;
        // the server's words, never an empty table that looks like "no visits"
        this.report = null;
        this.error = (err && err.message) || 'Could not load the report. Please try again.';
      } finally {
        if (this.requestId === mine) this.loading = false;
      }
    },

    rememberProviders(list) {
      const next = Object.assign({}, this.seen);
      (Array.isArray(list) ? list : []).forEach((p) => {
        if (p && p.provNum) next[p.provNum] = (typeof p.provName === 'string' && p.provName.trim()) || `Provider #${p.provNum}`;
      });
      this.seen = next;
    },

    sortBy(key) {
      // the first click starts with the useful end (the longest wait / lateness first, the rest A to Z / earliest first),
      // the second reverses it, the third gives the server's order back
      const first = key === 'wait' || key === 'late' ? -1 : 1;
      if (this.sortKey === key) {
        if (this.sortDir === first) this.sortDir = -first;
        else { this.sortKey = ''; this.sortDir = 1; }
      } else {
        this.sortKey = key;
        this.sortDir = first;
      }
    },

    ariaSort(col) {
      if (!col.sortable || this.sortKey !== col.key) return null;
      return this.sortDir === 1 ? 'ascending' : 'descending';
    },

    showMore() {
      this.visibleCount += PAGE_SIZE;
    },

    /** The visits (every one, not just the rows drawn) as a CSV of the practice's own times. */
    exportCsv() {
      if (!this.canExport) return;
      const { headers, rows } = visitsCsv(this.report.visits, { tz: this.zone, roomLabel: (n) => this.roomLabels[n] || '' });
      const filename = exportFilename('practiceOpsVisits', { from: this.shown.from, to: this.shown.to }, 'csv');
      this.triggerDownload(filename, toCsv(headers, rows));
    },

    /** The Blob + temporary anchor pattern the other exports use (ListDownloadMenu.vue). */
    triggerDownload(filename, text) {
      const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style lang="scss" scoped>
// Light-only: this page is not themed. Teal #0f7259 is the accent, amber / red only mean "over the waiting limit".
$ink: #0c0f0e;
$ink-sub: #3a4240;
$muted: #707876;
$border: #dfe4e3;
$hair: #eef2f1;
$wash: #f7faf9;
$teal: #0f7259;
$amber-text: #b45309;
$red-text: #c2333a;

.ops-report {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 1320px;
  min-width: 0;
  margin: 0 auto;
  padding: 4px 0 8px;
  color: $ink;
  // the app's base weight is 300 (Light), which makes the muted lines hard to read; the report's text is Regular unless it says otherwise
  font-weight: 400;
}

.ops-report__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

// ── header ──
.ops-report__back {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px 0;
  border: 0;
  background: none;
  color: $teal;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  svg { display: block; }
  &:focus-visible { outline: 2px solid #4fa98d; outline-offset: 2px; border-radius: 4px; }
}

.ops-report__head {
  display: flex;
  align-items: flex-end;
  gap: 16px;
}

.ops-report__intro { flex: 1; min-width: 0; }

.ops-report__title {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.ops-report__lede {
  margin: 4px 0 0;
  font-size: 13.5px;
  color: $muted;
}

.ops-report__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: none;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #c8cfcd;
  border-radius: 10px;
  background: #ffffff;
  color: $ink;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  svg { display: block; flex: none; }
  &:hover:not(:disabled) { background: $wash; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid #4fa98d; outline-offset: 2px; }
}

.ops-report__btn--primary {
  border-color: $teal;
  background: $teal;
  color: #ffffff;
  &:hover:not(:disabled) { background: #0b5c49; }
}

// ── the one filter row ──
.ops-report__filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 12px;
  min-width: 0;
}

.ops-report__pills {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px;
  max-width: 100%;
  padding: 3px;
  border-radius: 10px;
  background: #eff3f2;
}

.ops-report__pill {
  height: 30px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: $ink-sub;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:focus-visible { outline: 2px solid #4fa98d; outline-offset: 1px; }
  &.is-on {
    background: #ffffff;
    color: $teal;
    box-shadow: 0 1px 2px rgba(12, 15, 14, 0.1);
  }
}

.ops-report__custom {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: $muted;
  font-size: 13px;
}

.ops-report__date {
  width: 150px;
  max-width: 100%;
  height: 38px;
  padding: 0 10px;
  border: 1px solid #c8cfcd;
  border-radius: 8px;
  background: #ffffff;
  color: $ink;
  font-family: inherit;
  font-size: 14px;
}

.ops-report__prov { margin: 0 0 0 auto; }

.ops-report__select {
  width: 220px;
  max-width: 100%;
  height: 38px;
  padding: 0 10px;
  border: 1px solid #c8cfcd;
  border-radius: 8px;
  background: #ffffff;
  color: $ink;
  font-family: inherit;
  font-size: 14px;
}

.ops-report__range-error {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: $red-text;
}

// ── states ──
.ops-report__state {
  max-width: 520px;
  margin: 24px auto;
  padding: 24px;
  text-align: center;
  border: 1px solid $border;
  border-radius: 14px;
  background: #ffffff;

  &.is-dim { opacity: 0.55; }
}

.ops-report__state--error { border-color: #f0c4c6; background: #fff8f8; }

.ops-report__state-title {
  margin: 0 0 6px;
  font-size: 17px;
  font-weight: 800;
}

.ops-report__state-text {
  margin: 0 0 14px;
  color: $ink-sub;
  overflow-wrap: anywhere;
}

.ops-report__state-text:last-child { margin-bottom: 0; }

.ops-report__skeleton { display: flex; flex-direction: column; gap: 12px; }
.ops-report__skel-tiles { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.ops-report__skel-tile,
.ops-report__skel-chart {
  display: block;
  border-radius: 14px;
  background: $hair;
}
.ops-report__skel-tile { height: 104px; }
.ops-report__skel-chart { height: 250px; }

.ops-report__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  transition: opacity 0.12s;

  &.is-dim { opacity: 0.55; }
}

// ── tiles ──
.ops-report__tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.ops-report__tile {
  min-width: 0;
  padding: 16px 18px;
  border: 1px solid $border;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(12, 15, 14, 0.05);
}

.ops-report__tile-label {
  font-size: 12.5px;
  font-weight: 600;
  color: $muted;
}

.ops-report__tile-value {
  margin-top: 4px;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.ops-report__tile.is-a .ops-report__tile-value { color: $amber-text; }

.ops-report__tile-sub {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.35;
  color: $muted;
}

// ── cards + charts ──
.ops-report__charts {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
}

.ops-report__card {
  min-width: 0;
  padding: 16px 18px;
  border: 1px solid $border;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(12, 15, 14, 0.05);
}

.ops-report__card--flush {
  padding: 0;
  overflow: hidden;
}

.ops-report__card-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 12px;
}

.ops-report__card-head--pad {
  padding: 14px 18px;
  border-bottom: 1px solid $border;
}

.ops-report__card-title {
  flex: 1;
  font-size: 15px;
  font-weight: 800;
}

.ops-report__card-note {
  font-size: 12.5px;
  color: $muted;
}

.ops-report__card-sub {
  margin: 2px 0 8px;
  font-size: 12.5px;
  color: $muted;
}

.ops-report__key {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: $muted;
}

.ops-report__key-line {
  width: 18px;
  border-top: 2px dotted #cb8309;
}

.ops-report__key-box {
  width: 10px;
  height: 10px;
  border-radius: 3px;
}
.ops-report__key-box--booked { background: #c8cfcd; }
.ops-report__key-box--actual { background: $teal; }

.ops-report__seg {
  display: inline-flex;
  padding: 2px;
  border-radius: 8px;
  background: #eff3f2;
}

.ops-report__seg-btn {
  height: 24px;
  padding: 0 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: $ink-sub;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:focus-visible { outline: 2px solid #4fa98d; outline-offset: 1px; }
  &.is-on { background: #ffffff; color: $teal; box-shadow: 0 1px 2px rgba(12, 15, 14, 0.1); }
}

.ops-report__none {
  margin: 28px 0;
  text-align: center;
  font-size: 13.5px;
  color: $muted;
}

// ── tables ──
.ops-report__scroll { overflow-x: auto; }

.ops-report__mini {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-variant-numeric: tabular-nums;

  th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid $hair; white-space: nowrap; color: $ink; text-transform: none; letter-spacing: normal; }
  thead th { font-size: 11.5px; font-weight: 700; color: $muted; background: $wash; }
  tbody th { font-weight: 700; font-size: 13px; }
  tbody tr:last-child th, tbody tr:last-child td { border-bottom: 0; }
}

.ops-report__mini--pad {
  th:first-child, td:first-child { padding-left: 18px; }
  th:last-child, td:last-child { padding-right: 18px; }
}

.ops-report__table {
  width: 100%;
  min-width: 1120px;
  border-collapse: collapse;
  font-size: 13px;
  font-variant-numeric: tabular-nums;

  th, td { padding: 9px 6px; text-align: left; vertical-align: middle; white-space: nowrap; border-bottom: 1px solid $hair; color: $ink; text-transform: none; letter-spacing: normal; }
  th:first-child, td:first-child { padding-left: 18px; }
  th:last-child, td:last-child { padding-right: 18px; }
  td:first-child { position: sticky; left: 0; z-index: 1; background: #ffffff; }
  thead th:first-child { position: sticky; left: 0; z-index: 2; }
  thead th { background: $wash; border-bottom: 1px solid $border; font-size: 11.5px; font-weight: 700; color: $muted; }
  tbody tr:hover td { background: $wash; }
  tbody tr:last-child td { border-bottom: 0; }
  .is-muted { color: #9ca4a2; }
}

.ops-report__sort {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;

  &.is-on { color: $ink; }
  &:focus-visible { outline: 2px solid #4fa98d; outline-offset: 2px; border-radius: 3px; }
}

.ops-report__arrow { font-size: 9px; min-width: 9px; }

.ops-report__who { min-width: 150px; }

.ops-report__name {
  display: block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13.5px;
  font-weight: 700;
}

.ops-report__day {
  display: block;
  font-size: 11.5px;
  font-weight: 400;
  color: $muted;
}

// the wait: amber / red by the SAME threshold as the board's card border; the weight is the second channel, never colour alone
.ops-report__wait.is-amber { color: $amber-text; font-weight: 700; }
.ops-report__wait.is-red { color: $red-text; font-weight: 800; }
.ops-report__wait.is-none { color: #9ca4a2; }
.ops-report__live { font-size: 11.5px; font-weight: 600; color: $muted; }
.ops-report__live--block { display: block; font-weight: 500; }

.ops-report__legacy {
  margin: 0;
  padding: 10px 18px;
  border-bottom: 1px solid $hair;
  background: #fffaf0;
  font-size: 12.5px;
  color: #7a5200;
}

.ops-report__legacy-chip {
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: #eef2f1;
  color: #3a4240;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.ops-report__more {
  display: flex;
  justify-content: center;
  padding: 12px;
  border-top: 1px solid $hair;
}

// ── phone: a card per visit ──
.ops-report__cards {
  margin: 0;
  padding: 0;
  list-style: none;
}

.ops-vcard {
  position: relative;
  padding: 12px 14px;
  border-bottom: 1px solid $hair;

  &:last-child { border-bottom: 0; }
}

.ops-vcard__top {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.ops-vcard__who { flex: 1; min-width: 0; }

.ops-vcard__name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14.5px;
  font-weight: 800;
}

.ops-vcard__meta {
  display: block;
  margin-top: 1px;
  font-size: 12px;
  color: $muted;
}

.ops-vcard__top .ops-report__wait {
  flex: none;
  font-size: 15px;
  font-weight: 800;
  text-align: right;
}

.ops-vcard__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 12px;
  margin: 10px 0 0;

  dt { font-size: 11px; font-weight: 600; color: $muted; }
  dd { margin: 0; font-size: 13px; font-variant-numeric: tabular-nums; }
}

.ops-vcard .ops-report__legacy-chip { margin: 8px 0 0; }

// ── narrower than a wide desktop: the charts stack ──
@media (max-width: 980px) {
  .ops-report__charts { grid-template-columns: minmax(0, 1fr); }
}

// ── phone (the `phone` prop, < 700 px) ──
.ops-report--phone {
  gap: 12px;

  .ops-report__title { font-size: 25px; }
  .ops-report__lede { font-size: 13px; }
  .ops-report__btn { padding: 0 10px; }
  .ops-report__head { align-items: flex-start; }
  // the pills scroll inside their own box (never the page), without a scrollbar drawn under them
  .ops-report__pills { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; }
  .ops-report__pills::-webkit-scrollbar { display: none; }
  .ops-report__prov { margin: 0; flex: 1 1 100%; }
  .ops-report__select { width: 100%; }
  .ops-report__custom { flex: 1 1 100%; }
  // 16px in px: iOS Safari zooms in on focus below it and never zooms back (ios-focus-zoom-guardrail)
  .ops-report__date { flex: 1; width: auto; font-size: 16px; }
  .ops-report__select { font-size: 16px; }
  .ops-report__tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .ops-report__tile { padding: 12px 14px; }
  .ops-report__tile-value { font-size: 24px; }
  .ops-report__tile-sub { display: none; }
  .ops-report__skel-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .ops-report__card { padding: 14px; }
  .ops-report__card--flush { padding: 0; }
  .ops-report__card-head--pad { padding: 12px 14px; }
  .ops-report__legacy { padding: 10px 14px; }
}
</style>
